'use client';

import { Flex, freddyTheme, Skeleton } from '@coolblue-development/freddy';
import { Image } from '@coolblue-development/next-image/client';
import React, { useEffect, useRef, useState } from 'react';

type Props = {
  imageId: string;
  isFullWidthImage?: boolean;
};

export const CardImage = ({ imageId, isFullWidthImage }: Props) => {
  const [isImageLoaded, setIsImageLoaded] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(682);
  const [height, setHeight] = useState(420);

  const MOBILE_IMAGE_CONTAINER_HEIGHT = isFullWidthImage ? '195px' : 'auto';
  const MOBILE_IMAGE_CONTAINER_WIDTH = isFullWidthImage ? '100%' : '140px';

  useEffect(() => {
    if (containerRef.current?.querySelector('img')?.complete) {
      setIsImageLoaded(true);
    }
  }, []);

  useEffect(() => {
    const resizeObserver = new ResizeObserver(() => {
      const clientWidth = containerRef.current?.clientWidth
        ? containerRef.current?.clientWidth + 100 // Increasing the size to improve the image quality due to the incorrect aspect ratio of the image
        : 682;
      const clientHeight = containerRef.current?.clientHeight
        ? containerRef.current?.clientHeight + 100 // Increasing the size to improve the image quality due to the incorrect aspect ratio of the image
        : 420;
      setWidth(clientHeight > clientWidth ? clientWidth * 2 : clientWidth); // If the container is taller than it is wider, we set the width to double the container's width to ensure the image quality due to the incorrect aspect ratio of the image
      setHeight(clientHeight);
    });
    if (containerRef.current) resizeObserver.observe(containerRef.current);
    return () => resizeObserver.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerRef.current]);

  return (
    <Flex
      ref={containerRef}
      sx={{
        height: MOBILE_IMAGE_CONTAINER_HEIGHT,
        width: MOBILE_IMAGE_CONTAINER_WIDTH,
        minWidth: MOBILE_IMAGE_CONTAINER_WIDTH,
        justifyContent: 'center',
        overflow: 'hidden',
        position: 'relative',
        '> img': {
          overflow: 'hidden'
        },
        [freddyTheme.breakpoint.minWidth('xl')]: {
          height: '100%',
          width: '100%'
        }
      }}
    >
      {!isImageLoaded && (
        <Skeleton loading={true} className='image-skeleton' height='100%' width='100%' />
      )}
      <Image
        className={isImageLoaded ? 'visible-image' : 'loading-image'}
        imageId={imageId}
        fileCollection='content'
        objectFit='cover'
        width={width}
        height={height}
        fullHeight
        {...(isFullWidthImage && { fullWidth: true })}
        alt=''
        loading='lazy'
        fetchPriority='low'
        onLoad={() => setIsImageLoaded(true)}
      />
    </Flex>
  );
};
